import type { ReactNode, ComponentType } from "react";
import { ArrowLeft20Regular } from "@fluentui/react-icons";

interface PageHeaderAction {
  label: string;
  icon?: ComponentType<{ className?: string }>;
  onClick: () => void;
  disabled?: boolean;
}

interface PageHeaderBackButton {
  onClick: () => void;
}

interface PageHeaderProps {
  title: string;
  subtitle?: string;
  // Optional status/state badge rendered inline next to the title (state is an
  // attribute of the entity, not an action — so it belongs by the title).
  titleBadge?: ReactNode;
  actions?: ReactNode;
  tabs?: ReactNode;
  children?: ReactNode;
  primaryAction?: PageHeaderAction;
  secondaryAction?: PageHeaderAction;
  backButton?: PageHeaderBackButton;
}

export function PageHeader({
  title,
  subtitle,
  titleBadge,
  actions,
  tabs,
  children,
  primaryAction,
  secondaryAction,
  backButton,
}: PageHeaderProps) {
  const builtActions =
    primaryAction || secondaryAction ? (
      <>
        {secondaryAction && (
          <button
            onClick={secondaryAction.onClick}
            disabled={secondaryAction.disabled}
            className="h-10 px-4 bg-[var(--secondary)] border border-[var(--border)] text-[var(--secondary-foreground)] hover:bg-[var(--accent)] transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {secondaryAction.icon && (
              <secondaryAction.icon className="w-5 h-5" />
            )}
            {secondaryAction.label}
          </button>
        )}
        {primaryAction && (
          <button
            onClick={primaryAction.onClick}
            disabled={primaryAction.disabled}
            className="h-10 px-4 bg-[var(--primary)] text-[var(--primary-foreground)] hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            style={{ borderRadius: "var(--radius-button)" }}
          >
            {primaryAction.icon && (
              <primaryAction.icon className="w-5 h-5" />
            )}
            {primaryAction.label}
          </button>
        )}
      </>
    ) : null;

  return (
    <div className="bg-card border-b border-border">
      <div
        className="px-4 md:px-6"
        style={{
          paddingTop: "var(--spacing-page-header-top)",
          paddingBottom:
            !tabs && !children
              ? "var(--spacing-page-header-bottom)"
              : undefined,
        }}
      >
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
              <div className="flex items-center gap-3 flex-1 min-w-0">
                {backButton && (
                  <button
                    onClick={backButton.onClick}
                    className="p-2 hover:bg-[var(--accent)] transition-colors hidden md:flex"
                    style={{ borderRadius: "var(--radius)" }}
                    aria-label="Voltar"
                  >
                    <ArrowLeft20Regular className="text-card-foreground" />
                  </button>
                )}

                <h1
                  className="text-foreground"
                  style={{
                    fontSize: "var(--text-page-title)",
                    fontWeight: "var(--font-weight-semi-bold)",
                    lineHeight: "1.3",
                  }}
                >
                  {title}
                </h1>
                {titleBadge}
              </div>

              {(actions || builtActions) && (
                <div className="flex md:hidden items-stretch gap-2 flex-wrap">
                  {actions || builtActions}
                </div>
              )}
            </div>

            {subtitle && (
              <p
                style={{
                  fontSize: "var(--text-base)",
                  marginTop: "var(--spacing-title-subtitle)",
                  color: "var(--subtitle-text)",
                }}
              >
                {subtitle}
              </p>
            )}
          </div>

          {(actions || builtActions) && (
            <div className="hidden md:flex items-stretch gap-2 flex-wrap flex-shrink-0">
              {actions || builtActions}
            </div>
          )}
        </div>

        {tabs && (
          <div
            className="flex gap-0 border-b-0 overflow-x-auto scrollbar-hide -mx-4 md:-mx-6 px-4 md:px-6"
            style={{
              marginTop: "var(--spacing-subtitle-tabs)",
              marginBottom: "calc(-1px)",
            }}
          >
            {tabs}
          </div>
        )}

        {children}
      </div>
    </div>
  );
}

interface PageHeaderTabProps {
  label: string;
  isActive: boolean;
  onClick: () => void;
  disabled?: boolean;
}

export function PageHeaderTab({
  label,
  isActive,
  onClick,
  disabled = false,
}: PageHeaderTabProps) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`px-4 py-2 border-b-2 transition-colors whitespace-nowrap min-h-[44px] disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-secondary-text disabled:hover:border-transparent ${
        isActive
          ? "border-primary text-primary"
          : "border-transparent text-secondary-text hover:text-foreground hover:border-border"
      }`}
      style={{
        fontWeight: isActive
          ? "var(--font-weight-semi-bold)"
          : "var(--font-weight-normal)",
      }}
    >
      {label}
    </button>
  );
}
